import { useState, useEffect } from "react";
import { fetchAdminFeedback, updateAdminFeedback } from "../services/apiService";

const TYPES = {
  bug: { emoji: "🐞", label: "Báo lỗi", color: "#EF4444" },
  feature: { emoji: "💡", label: "Tính năng", color: "#3B82F6" },
  suggestion: { emoji: "💬", label: "Góp ý", color: "#10B981" },
};

const STATUSES = [
  { id: "new", label: "Mới", color: "#6B7280" },
  { id: "seen", label: "Đã xem", color: "#3B82F6" },
  { id: "working", label: "Đang xử lý", color: "#F59E0B" },
  { id: "done", label: "Xong", color: "#10B981" },
];

export default function AdminFeedbackView() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminFeedback().then(data => {
      if (data === "forbidden") {
        setForbidden(true);
      } else if (data) {
        setFeedbacks(data);
      }
      setLoading(false);
    });
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    const result = await updateAdminFeedback(id, { status: newStatus });
    if (result) {
      setFeedbacks(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
    }
  };

  const handleReply = async (id) => {
    if (!replyText.trim()) return;
    setSaving(true);
    const result = await updateAdminFeedback(id, { admin_reply: replyText.trim(), status: "seen" });
    if (result) {
      setFeedbacks(prev => prev.map(f => f.id === id ? { ...f, admin_reply: replyText.trim(), status: result.status } : f));
      setReplyingId(null);
      setReplyText("");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
        Đang tải...
      </div>
    );
  }

  if (forbidden) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔒</div>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>Không có quyền truy cập</div>
        <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "8px" }}>Trang này chỉ dành cho admin.</div>
      </div>
    );
  }

  // Filter
  const filtered = feedbacks.filter(f => {
    if (filterType !== "all" && f.type !== filterType) return false;
    if (filterStatus !== "all" && f.status !== filterStatus) return false;
    return true;
  });

  // Stats
  const stats = {
    total: feedbacks.length,
    new: feedbacks.filter(f => f.status === "new").length,
    bug: feedbacks.filter(f => f.type === "bug").length,
    feature: feedbacks.filter(f => f.type === "feature").length,
    suggestion: feedbacks.filter(f => f.type === "suggestion").length,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <div style={{ fontSize: "11px", letterSpacing: "2px", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "4px" }}>Admin</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: "var(--text-primary)" }}>Quản lý đóng góp</div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", overflowX: "auto" }}>
        <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "60px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "var(--text-primary)" }}>{stats.total}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Tổng</div>
        </div>
        <div style={{ flex: 1, background: stats.new > 0 ? "#EF444415" : "var(--bg-card)", border: `1px solid ${stats.new > 0 ? "#EF444444" : "var(--border-secondary)"}`, borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "60px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: stats.new > 0 ? "#EF4444" : "var(--text-muted)" }}>{stats.new}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Chưa xem</div>
        </div>
        <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "60px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#EF4444" }}>{stats.bug}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>🐞 Bug</div>
        </div>
        <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "60px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#3B82F6" }}>{stats.feature}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>💡 Feature</div>
        </div>
        <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "60px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#10B981" }}>{stats.suggestion}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>💬 Góp ý</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ background: "var(--bg-card)", border: "1px solid var(--border-primary)", borderRadius: "8px", padding: "6px 10px", color: "var(--text-primary)", fontSize: "12px" }}>
          <option value="all">Tất cả loại</option>
          <option value="bug">🐞 Bug</option>
          <option value="feature">💡 Tính năng</option>
          <option value="suggestion">💬 Góp ý</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ background: "var(--bg-card)", border: "1px solid var(--border-primary)", borderRadius: "8px", padding: "6px 10px", color: "var(--text-primary)", fontSize: "12px" }}>
          <option value="all">Tất cả trạng thái</option>
          <option value="new">Mới</option>
          <option value="seen">Đã xem</option>
          <option value="working">Đang xử lý</option>
          <option value="done">Xong</option>
        </select>
        <div style={{ fontSize: "12px", color: "var(--text-muted)", display: "flex", alignItems: "center" }}>
          {filtered.length}/{feedbacks.length} mục
        </div>
      </div>

      {/* Feedback list */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {filtered.map(fb => {
          const t = TYPES[fb.type] || TYPES.suggestion;
          const date = new Date(fb.created_at);
          const dateStr = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
          const isReplying = replyingId === fb.id;

          return (
            <div key={fb.id} style={{
              background: "var(--bg-card)",
              border: `1px solid ${fb.status === "new" ? "#EF444444" : "var(--border-secondary)"}`,
              borderRadius: "14px", padding: "16px",
              borderLeft: `3px solid ${t.color}`,
            }}>
              {/* Top row: type + user + date */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", flexWrap: "wrap", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>{t.emoji}</span>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: t.color }}>{t.label}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {fb.user_info?.avatar && (
                    <img src={fb.user_info.avatar} alt="" style={{ width: 20, height: 20, borderRadius: "50%" }} />
                  )}
                  <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{fb.user_info?.name || "Unknown"}</span>
                  <span style={{ fontSize: "10px", color: "var(--text-dim)" }}>{dateStr}</span>
                </div>
              </div>

              {/* Message */}
              <div style={{ fontSize: "13px", color: "var(--text-primary)", lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: "12px" }}>
                {fb.message}
              </div>

              {/* Existing reply */}
              {fb.admin_reply && !isReplying && (
                <div style={{ background: "#3B82F610", border: "1px solid #3B82F633", borderRadius: "8px", padding: "10px", marginBottom: "10px" }}>
                  <div style={{ fontSize: "10px", fontWeight: "600", color: "#3B82F6", marginBottom: "4px" }}>💬 Phản hồi của bạn</div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5 }}>{fb.admin_reply}</div>
                </div>
              )}

              {/* Actions: status + reply */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "4px" }}>
                  {STATUSES.map(s => (
                    <button key={s.id} onClick={() => handleStatusChange(fb.id, s.id)}
                      style={{
                        background: fb.status === s.id ? `${s.color}20` : "transparent",
                        border: `1px solid ${fb.status === s.id ? s.color : "var(--border-primary)"}`,
                        borderRadius: "6px", padding: "3px 8px", cursor: "pointer",
                        fontSize: "10px", fontWeight: "600",
                        color: fb.status === s.id ? s.color : "var(--text-muted)",
                      }}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => { setReplyingId(isReplying ? null : fb.id); setReplyText(fb.admin_reply || ""); }}
                  style={{
                    background: "transparent", border: "1px solid var(--border-primary)", borderRadius: "6px",
                    padding: "3px 10px", cursor: "pointer", fontSize: "10px", color: "var(--text-muted)",
                  }}>
                  {isReplying ? "Hủy" : fb.admin_reply ? "Sửa phản hồi" : "Phản hồi"}
                </button>
              </div>

              {/* Reply input */}
              {isReplying && (
                <div style={{ marginTop: "10px" }}>
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Nhập phản hồi cho người dùng..."
                    style={{
                      width: "100%", minHeight: "80px", padding: "10px", fontSize: "12px", lineHeight: 1.5,
                      background: "var(--bg-main)", border: "1px solid var(--border-primary)", borderRadius: "8px",
                      color: "var(--text-primary)", resize: "vertical", outline: "none", fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                    <button onClick={() => { setReplyingId(null); setReplyText(""); }}
                      style={{ background: "transparent", border: "1px solid var(--border-primary)", borderRadius: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "11px", color: "var(--text-muted)" }}>
                      Hủy
                    </button>
                    <button onClick={() => handleReply(fb.id)} disabled={!replyText.trim() || saving}
                      style={{
                        background: replyText.trim() && !saving ? "#3B82F6" : "var(--border-primary)",
                        border: "none", borderRadius: "8px", padding: "6px 14px", cursor: replyText.trim() && !saving ? "pointer" : "not-allowed",
                        fontSize: "11px", fontWeight: "600", color: "#fff",
                      }}>
                      {saving ? "Đang lưu..." : "Gửi phản hồi"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)", fontSize: "13px" }}>
          {feedbacks.length === 0 ? "Chưa có đóng góp nào." : "Không có mục nào khớp bộ lọc."}
        </div>
      )}
    </div>
  );
}
